/**
 * Product Info & Media – Recharge subscription detection
 * Detects if the Recharge app has injected the subscription widget (recharge-subscription-widget).
 * When present, moves add-to-cart-component below the widget so "Subscribe & save" appears above the ATC button.
 */
(function () {
  'use strict';


  var WIDGET_SELECTOR = 'recharge-subscription-widget';
  var ATC_SELECTOR = 'add-to-cart-component';
  var TIMEOUT_MS = 15000; /* wait up to 15s for async injection */

  function hasSubscriptionWidget() {
    return document.querySelector(WIDGET_SELECTOR) !== null;
  }

  function logResult(hasSub) {
    if (hasSub) {
    } else {
    }
  }

  var SVG_CHECKED = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="19" height="19" rx="9.5" stroke="#0C2045"/><circle cx="10" cy="10" r="5.5" fill="#0C2045" stroke="#0C2045"/></svg>';
  var SVG_UNCHECKED = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="9.5" stroke="#0C2045"/></svg>';
  var WRAPPER_CLASS = 'adegen-rc-icon-wrap';

  /**
   * Update only our overlay's SVG. Do NOT replace shadowRoot.innerHTML so Recharge's DOM stays intact and insertBefore does not break.
   */
  function setRadioIconSvg(iconEl) {
    if (!iconEl || !iconEl.shadowRoot) return;
    var isChecked = iconEl.hasAttribute('checked');
    var wrap = iconEl.shadowRoot.querySelector('.' + WRAPPER_CLASS);
    if (wrap) {
      wrap.innerHTML = isChecked ? SVG_CHECKED : SVG_UNCHECKED;
      return;
    }
    /* First run: inject style (hide Recharge SVG) + our wrapper with SVG, leave rest of shadow DOM untouched */
    var style = document.createElement('style');
    style.textContent = 'svg.rc-checked-icon, svg.rc-unchecked-icon { display: none !important; } .' + WRAPPER_CLASS + ' { display: block; width: 20px; height: 20px; flex-shrink: 0; } .' + WRAPPER_CLASS + ' svg { display: block; width: 100%; height: 100%; }';
    iconEl.shadowRoot.appendChild(style);
    var wrapper = document.createElement('div');
    wrapper.className = WRAPPER_CLASS;
    wrapper.innerHTML = isChecked ? SVG_CHECKED : SVG_UNCHECKED;
    iconEl.shadowRoot.appendChild(wrapper);
  }

  /** Get the recharge-subscription-widget that owns this icon (icon lives in widget's shadow root). */
  function getWidgetFromIcon(iconEl) {
    var root = iconEl && iconEl.getRootNode ? iconEl.getRootNode() : null;
    return root && root.host ? root.host : null;
  }

  /** Refresh SVG on every rc-radio-icon in the widget (handles Recharge re-rendering / new nodes). */
  function refreshAllRadioIconsInWidget(widget) {
    if (!widget) return;
    var root = widget.shadowRoot || widget;
    var icons = root.querySelectorAll('rc-radio-icon');
    icons.forEach(function (el) { setRadioIconSvg(el); });
    syncDiscountBadgeFromWidget(widget);
  }

  /**
   * Read the selected option's badge (e.g. "Save up to 25%") from the Recharge widget, parse the percent,
   * and update the section's product-info-and-media__discount-badge. Hide the badge if the selected option has no discount.
   * @returns {boolean} true if we could read the selected option's badge state (badge element found or confirmed absent).
   */
  function syncDiscountBadgeFromWidget(widget) {
    if (!widget) return false;
    var section = widget.closest ? widget.closest('.product-info-and-media') : null;
    var badgeEl = section && section.querySelector('[data-discount-badge]');
    if (!badgeEl) return false;
    /* If sub-only mode locked the badge, don't let this function hide it */
    if (badgeEl.dataset.subOnlyLocked === '1') return true;
    var textEl = badgeEl.querySelector('.product-info-and-media__discount-badge-text');
    if (!textEl) return false;
    var root = widget.shadowRoot || widget;
    var selectedOption = root.querySelector('[rc-selected]');
    if (!selectedOption) return false;
    var badgeInOption = selectedOption.querySelector('.rc-purchase-option__badge');
    var badgeText = badgeInOption ? (badgeInOption.textContent || '').trim() : '';
    var percentMatch = badgeText.match(/(\d+)\s*%/);
    var percent = percentMatch ? percentMatch[1] : null;
    if (percent) {
      badgeEl.setAttribute('data-savings-percent', percent);
      textEl.textContent = 'Save ' + percent + '%';
      badgeEl.removeAttribute('hidden');
    } else {
      badgeEl.removeAttribute('data-savings-percent');
      textEl.textContent = '';
      badgeEl.setAttribute('hidden', '');
    }
    var isOnetime = selectedOption && ((selectedOption.getAttribute('part') || '').indexOf('onetime') !== -1);
    return !!badgeInOption || !!isOnetime;
  }

  /** Poll for Recharge option badge until it's in the DOM or max wait; then sync section discount badge. */
  function syncDiscountBadgeWhenReady(widget) {
    if (!widget) return;
    var POLL_INTERVAL_MS = 100;
    var MAX_WAIT_MS = 2500;
    var start = Date.now();
    function poll() {
      if (Date.now() - start > MAX_WAIT_MS) return;
      var done = syncDiscountBadgeFromWidget(widget);
      if (done) return;
      window.setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();
  }

  function observeRadioIcon(iconEl, index) {
    if (!iconEl || iconEl.dataset.adegenRadioPatched === '1') return;
    setRadioIconSvg(iconEl);
    iconEl.dataset.adegenRadioPatched = '1';
    var observer = new MutationObserver(function () {
      /* Recharge may replace nodes on option change; refresh all icons so the newly selected one gets our SVG. */
      var widget = getWidgetFromIcon(iconEl);
      refreshAllRadioIconsInWidget(widget);
    });
    observer.observe(iconEl, { attributes: true, attributeFilter: ['checked'] });
  }

  /**
   * Replace Recharge's internal icons with custom Adegen SVGs (checked = filled circle, unchecked = outline).
   * Patches each rc-radio-icon's shadow root and observes `checked` so toggling options updates the icon.
   * rc-radio-icon elements live inside the widget's shadow root, so we must query there.
   */
  function patchRechargeRadioIcons(widget) {
    if (!widget) return;
    var root = widget.shadowRoot || widget;
    var icons = root.querySelectorAll('rc-radio-icon');
    icons.forEach(function (el, i) { observeRadioIcon(el, i); });
    injectWidgetVwStyles(widget);
  }

  /**
   * Inject a <style> into the widget's shadow root so padding overrides beat the
   * constructed stylesheet Recharge uses internally. ::part() from the document
   * loses to adopted stylesheets in the shadow cascade, but an injected <style>
   * inside the shadow root wins via source order.
   */
  var VW_STYLE_ID = 'adegen-rc-vw-overrides';
  var VW_STYLE_CSS = [
    /* Base override – beat constructed stylesheet padding */
    '.rc-purchase-option__label { padding: 16px 24px !important; }',
    '@media screen and (max-width: 1000px) {',
    '  .rc-purchase-option__label { padding: 12px !important; }',
    '  .rc-purchase-option__selector { font-size: 14px !important; }',
    '}'
  ].join('\n');

  function injectWidgetVwStyles(widget) {
    if (!widget || !widget.shadowRoot) return;
    if (widget.shadowRoot.querySelector('#' + VW_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = VW_STYLE_ID;
    style.textContent = VW_STYLE_CSS;
    widget.shadowRoot.appendChild(style);
  }

  /**
   * When Recharge re-renders options it can replace rc-radio-icon nodes, so our per-icon observers are lost.
   * Watch the widget's shadow root for DOM changes and re-patch so new icons get our SVG and observers.
   */
  function observeWidgetShadowForNewIcons(widget) {
    if (!widget || !widget.shadowRoot || widget.dataset.adegenWidgetObserved === '1') return;
    widget.dataset.adegenWidgetObserved = '1';
    var raf = null;
    var observer = new MutationObserver(function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        raf = null;
        patchRechargeRadioIcons(widget);
      });
    });
    observer.observe(widget.shadowRoot, { childList: true, subtree: true });
  }

  /**
   * Subscription-only detection.
   * When the Recharge widget has only a subscription option (no one-time purchase),
   * it renders a single label with the class "single-selection". In that case:
   * 1. Hide the label inside the widget (no need for the user to toggle anything).
   * 2. Change the ATC button text from "ADD TO CART" to "GET STARTED".
   */
  var SINGLE_SELECTION_CLASS = 'single-selection';
  var SUB_ONLY_ATC_TEXT = 'GET STARTED';

  /**
   * Hide widget + change ATC text immediately when sub-only is detected.
   * Discount badge is handled separately by pollSubOnlyDiscount.
   */
  function handleSubscriptionOnly(widget) {
    if (!widget) return;
    var root = widget.shadowRoot || widget;
    var singleLabel = root.querySelector('.rc-purchase-option__label.' + SINGLE_SELECTION_CLASS);
    if (!singleLabel) {
      return;
    }


    /* Hide the widget visually but keep it in DOM so shadow content still renders */
    widget.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none;';

    /* Change ATC button text */
    var section = widget.closest ? widget.closest('.product-info-and-media') : null;
    if (!section) section = document.querySelector('.product-info-and-media[data-section-id]');
    if (!section) return;

    var atcLabel = section.querySelector('.product-info-and-media__atc-label');
    if (atcLabel) {
      atcLabel.textContent = SUB_ONLY_ATC_TEXT;
    }

    /* Show discount badge: first try variant compare_at_price from DOM, then poll widget */
    showSubOnlyDiscount(widget, section);
  }

  /**
   * Show the discount badge for sub-only products.
   * Strategy:
   *   1. Try reading compare_at_price vs price from the variant pill data attributes.
   *   2. If that yields a percent, show it immediately.
   *   3. Also poll the widget shadow DOM as backup (widget is visibility:hidden so DOM renders).
   *   4. Lock the badge so syncDiscountBadgeFromWidget won't hide it.
   */
  function showSubOnlyDiscount(widget, section) {
    if (!widget || !section) return;

    function applyBadge(percent) {
      var badgeEl = section.querySelector('[data-discount-badge]');
      var textEl = badgeEl && badgeEl.querySelector('.product-info-and-media__discount-badge-text');
      if (badgeEl && textEl) {
        badgeEl.setAttribute('data-savings-percent', percent);
        badgeEl.dataset.subOnlyLocked = '1';
        textEl.textContent = 'Save ' + percent + '%';
        badgeEl.removeAttribute('hidden');
        badgeEl.style.removeProperty('display');
        return true;
      }
      return false;
    }

    /* Temporary: show variant/Liquid percentage while waiting for Recharge widget */
    var selectedPill = section.querySelector('.product-info-and-media__variant-pill--selected');
    if (selectedPill) {
      var price = parseInt(selectedPill.dataset.variantPrice, 10);
      var comparePrice = parseInt(selectedPill.dataset.variantComparePrice, 10);
      if (comparePrice && comparePrice > price) {
        var pct = Math.round((comparePrice - price) / comparePrice * 100);
        if (pct > 0) applyBadge(String(pct));
      }
    }
    if (!section.querySelector('[data-discount-badge]:not([hidden])')) {
      var badgeEl = section.querySelector('[data-discount-badge]');
      if (badgeEl) {
        var existing = badgeEl.getAttribute('data-savings-percent');
        if (existing && existing !== '' && parseInt(existing, 10) > 0) {
          applyBadge(existing);
        }
      }
    }

    /* Always poll widget shadow DOM for Recharge discount (overrides temporary value) */
    var POLL_INTERVAL_MS = 200;
    var MAX_WAIT_MS = 8000;
    var start = Date.now();

    function tryReadDiscount() {
      var root = widget.shadowRoot || widget;

      /* Strategy A: look for explicit discount text (e.g. rc-plans-button__discount) */
      var selectors = [
        '.rc-plans-button__discount',
        '.rc-purchase-option__badge',
        '.rc-selling-plan-group__badge',
        '[class*="discount"]'
      ];
      for (var i = 0; i < selectors.length; i++) {
        var els = root.querySelectorAll(selectors[i]);
        for (var j = 0; j < els.length; j++) {
          var txt = (els[j].textContent || '').trim();
          if (txt && txt.match(/\d+\s*%/)) {
            return { type: 'text', value: txt };
          }
        }
      }

      /* Strategy B: read two rc-price elements and calculate percent */
      var strikeEl = root.querySelector('.rc-price.strike-through');
      var priceEls = root.querySelectorAll('.rc-price:not(.strike-through)');
      var saleEl = priceEls.length ? priceEls[priceEls.length - 1] : null;

      if (strikeEl && saleEl) {
        var strikeTxt = (strikeEl.textContent || '').replace(/[^0-9.]/g, '');
        var saleTxt = (saleEl.textContent || '').replace(/[^0-9.]/g, '');
        var strikeVal = parseFloat(strikeTxt);
        var saleVal = parseFloat(saleTxt);
        if (strikeVal > 0 && saleVal > 0 && strikeVal > saleVal) {
          var pct = Math.round((strikeVal - saleVal) / strikeVal * 100);
          if (pct > 0) return { type: 'calc', value: String(pct) };
        }
      }

      return null;
    }

    function poll() {
      var result = tryReadDiscount();
      if (result) {
        var percent = result.type === 'text'
          ? (result.value.match(/(\d+)\s*%/) || [])[1]
          : result.value;
        if (percent) {
          applyBadge(percent);
          return;
        }
      }
      if (Date.now() - start < MAX_WAIT_MS) {
        window.setTimeout(poll, POLL_INTERVAL_MS);
      } else {
      }
    }
    poll();
  }

  /** Poll for the single-selection element inside the widget shadow DOM until found or max wait. */
  function handleSubscriptionOnlyWhenReady(widget) {
    if (!widget) return;
    var POLL_INTERVAL_MS = 150;
    var MAX_WAIT_MS = 5000;
    var start = Date.now();
    function poll() {
      if (Date.now() - start > MAX_WAIT_MS) return;
      var root = widget.shadowRoot || widget;
      var singleLabel = root.querySelector('.rc-purchase-option__label.' + SINGLE_SELECTION_CLASS);
      if (singleLabel) {
        handleSubscriptionOnly(widget);
        return;
      }
      window.setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();
  }

  /**
   * Refresh icons after any click inside the widget. Recharge may re-render in a way that doesn't
   * trigger our MutationObservers; running after click ensures we re-apply our SVG once they're done.
   */
  function installWidgetClickRefresh() {
    if (document.documentElement.dataset.adegenRechargeClickRefresh === '1') return;
    document.documentElement.dataset.adegenRechargeClickRefresh = '1';
    document.addEventListener('click', function (e) {
      var path = e.composedPath ? e.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (node && node.tagName && node.tagName.toLowerCase() === 'recharge-subscription-widget') {
          setTimeout(function (w) {
            return function () {
              refreshAllRadioIconsInWidget(w);
            };
          }(node), 100);
          break;
        }
      }
    }, true);
  }

  /**
   * Ensure "Subscribe & save" (widget) appears above the ADD TO CART button (atc).
   * - Same parent: move ATC below widget.
   * - Different parents (Recharge injects widget outside form): move widget into form before ATC so ATC stays in form.
   */
  function moveAtcBelowWidget(section) {
    var widget = section.querySelector(WIDGET_SELECTOR);
    var atc = section.querySelector(ATC_SELECTOR);
    if (!widget || !atc) return;

    if (widget.parentNode === atc.parentNode) {
      if (widget.nextElementSibling === atc) {
        return;
      }
      widget.parentNode.insertBefore(atc, widget.nextSibling);
      return;
    }

    /* Different parents: Recharge injects widget outside the form. Move widget into form before ATC so order is widget → ATC and ATC stays in form. */
    atc.parentNode.insertBefore(widget, atc);
  }

  function startWatching(section) {
    if (hasSubscriptionWidget()) {
      logResult(true);
      var widget = section.querySelector(WIDGET_SELECTOR) || document.querySelector(WIDGET_SELECTOR);
      moveAtcBelowWidget(section);
      patchRechargeRadioIcons(widget);
      observeWidgetShadowForNewIcons(widget);
      installWidgetClickRefresh();
      window.setTimeout(function () { patchRechargeRadioIcons(widget); }, 400);
      syncDiscountBadgeWhenReady(widget);
      handleSubscriptionOnlyWhenReady(widget);
      return;
    }


    var resolved = false;

    function resolve(hasSub) {
      if (resolved) return;
      resolved = true;
      if (hasSub) {
      } else {
      }
      logResult(hasSub);
      if (hasSub) {
        var w = document.querySelector(WIDGET_SELECTOR);
        moveAtcBelowWidget(section);
        patchRechargeRadioIcons(w);
        observeWidgetShadowForNewIcons(w);
        installWidgetClickRefresh();
        window.setTimeout(function () { patchRechargeRadioIcons(w); }, 400);
        syncDiscountBadgeWhenReady(w);
        handleSubscriptionOnlyWhenReady(w);
      }
    }

    var observer = new MutationObserver(function () {
      if (hasSubscriptionWidget()) {
        observer.disconnect();
        resolve(true);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    window.setTimeout(function () {
      observer.disconnect();
      resolve(hasSubscriptionWidget());
    }, TIMEOUT_MS);
  }

  function init() {
    var section = document.querySelector('.product-info-and-media[data-section-id]');
    if (!section) {
      window.setTimeout(init, 100);
      return;
    }

    /* Start watching after a short delay so Recharge has a chance to inject */
    window.setTimeout(function () {
      startWatching(section);
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
